import React, { useEffect, useState } from "react";
import { collection, doc, onSnapshot, query, where } from "firebase/firestore";
import { getBytes, ref as storageRef } from "firebase/storage";
import { BookLock, BookOpenCheck, Download, FileText, Loader2, ShieldCheck } from "lucide-react";
import { db, storage } from "../Lib/Firebase";

type Material = {
    id: string;
    title: string;
    description?: string;
    category?: string;
    fileName: string;
    storagePath: string;
    sizeBytes?: number;
};

export default function PaidStudyMaterials({ userId }: { userId: string }) {
    const [materials, setMaterials] = useState<Material[]>([]);
    const [entitled, setEntitled] = useState(false);
    const [entitlementLoading, setEntitlementLoading] = useState(true);
    const [loading, setLoading] = useState(false);
    const [downloadingId, setDownloadingId] = useState("");
    const [error, setError] = useState("");

    useEffect(() => onSnapshot(doc(db, "materialEntitlements", userId), (snapshot) => {
        setEntitled(snapshot.exists() && snapshot.data()?.active === true);
        setEntitlementLoading(false);
    }, (loadError) => {
        setError(loadError.message);
        setEntitlementLoading(false);
    }), [userId]);

    useEffect(() => {
        if (!entitled) {
            setMaterials([]);
            setLoading(false);
            return;
        }
        setLoading(true);
        const materialsQuery = query(collection(db, "materials"), where("status", "==", "published"));
        return onSnapshot(materialsQuery, (snapshot) => {
            setMaterials(snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as Material)));
            setLoading(false);
        }, (loadError) => {
            setError(loadError.message);
            setLoading(false);
        });
    }, [entitled]);

    const download = async (material: Material) => {
        setDownloadingId(material.id);
        setError("");
        try {
            const bytes = await getBytes(storageRef(storage, material.storagePath), 25 * 1024 * 1024);
            const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
            const link = document.createElement("a");
            link.href = url;
            link.download = material.fileName || `${material.title}.pdf`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } catch (downloadError: any) {
            setError(downloadError?.message || "The PDF could not be downloaded.");
        } finally {
            setDownloadingId("");
        }
    };

    return <section className="overflow-hidden rounded-3xl border border-blue-100 bg-white shadow-xl shadow-blue-900/5">
        <div className="bg-gradient-to-r from-[#061a4a] via-[#075dcc] to-[#38aee8] p-5 text-white sm:p-6"><div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center"><div><p className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.18em] text-blue-100"><BookOpenCheck className="h-4 w-4" />Candidate learning library</p><h3 className="mt-2 text-2xl font-black">Study Materials</h3><p className="mt-1 text-sm text-blue-100">Learning PDFs published by your MockZo administrator.</p></div><span className={`w-fit rounded-full px-3 py-1.5 text-xs font-black ${entitled ? "bg-emerald-300 text-emerald-950" : "bg-white/15 text-white"}`}>{entitled ? "Access active" : "Access required"}</span></div></div>

        {entitlementLoading ? <div className="flex items-center justify-center gap-2 p-10 text-sm font-bold text-blue-700"><Loader2 className="h-5 w-5 animate-spin" />Checking material access…</div>
        : !entitled ? <div className="p-6 text-center sm:p-8"><span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-slate-100 text-slate-400"><BookLock className="h-7 w-7" /></span><h4 className="mt-4 text-lg font-black text-slate-900">Paid library locked</h4><p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-slate-500">Complete a paid course enrollment to unlock administrator-published study materials in this profile.</p></div>
        : loading ? <div className="flex items-center justify-center gap-2 p-10 text-sm font-bold text-blue-700"><Loader2 className="h-5 w-5 animate-spin" />Loading your materials…</div>
        : <div className="p-5 sm:p-6"><div className="grid gap-3 md:grid-cols-2">{materials.map((material) => <article key={material.id} className="flex flex-col rounded-2xl border border-slate-200 bg-gradient-to-br from-white to-blue-50/50 p-4"><div className="flex items-start gap-3"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-blue-100 text-blue-700"><FileText className="h-5 w-5" /></span><div><h4 className="font-black text-slate-900">{material.title}</h4><p className="mt-1 text-xs font-bold uppercase tracking-wide text-blue-600">{material.category || "Course Learning"}</p></div></div><p className="mt-3 flex-1 text-sm leading-6 text-slate-500">{material.description || "Paid MockZo course study material."}</p><button type="button" onClick={() => download(material)} disabled={downloadingId === material.id} className="mt-4 inline-flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#075dcc] to-[#38aee8] px-4 py-2.5 text-sm font-black text-white shadow-md disabled:opacity-60">{downloadingId === material.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}{downloadingId === material.id ? "Preparing PDF…" : "Download PDF"}</button></article>)}{materials.length === 0 && <div className="md:col-span-2 rounded-2xl border border-dashed border-blue-200 bg-blue-50/50 p-8 text-center"><ShieldCheck className="mx-auto h-8 w-8 text-blue-400" /><h4 className="mt-3 font-black text-slate-800">Your access is active</h4><p className="mt-1 text-sm text-slate-500">New course PDFs will appear here as soon as the administrator publishes them.</p></div>}</div></div>}
        {error && <p role="alert" className="mx-5 mb-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-bold text-red-700 sm:mx-6 sm:mb-6">{error}</p>}
    </section>;
}
